/**
 * React component for rendering popout widgets.
 *
 * This component listens to the popout widgets store and opens a new browser window
 * for each widget added. It uses React portals to render the widget's React component
 * into the container of the new window. When a widget is removed from the store or
 * the popout window is closed, the widget is cleaned up accordingly.
 *
 * Dependencies:
 * - componentMap: Maps widget IDs to React components.
 * - usePopoutWidgetsStore: Zustand store for managing popout widgets.
 * - openPopoutWindow: Utility to open a new browser window and provide a container for rendering.
 */
import { componentMap } from "@/utils/componentMap";
import { ReactElement, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { usePopoutWidgetsStore } from "@/stores/PopoutWidgetsStore";
import { openPopoutWindow } from "@/utils/popout";
import { WidgetContainer } from "@/layout-components/WidgetContainer";
import { PortalProvider } from "@/shared/context/PortalContext/PortalContext";
import { DensityProvider } from "@/contexts/DensityContext";

function PopoutWidgets() {
  const popoutWidgets = usePopoutWidgetsStore((state) => state.widgets);

  const removePopoutWidget = usePopoutWidgetsStore(
    (state) => state.removeWidget
  );

  const [widgetPortals, setWidgetPortals] = useState<
    [ReactElement, HTMLDivElement][]
  >([]);

  // Keep track of which widgets have already been processed
  const processedWidgets = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (popoutWidgets.length === 0) {
      // Clear all when no widgets
      setWidgetPortals([]);
      processedWidgets.current.clear();
      return;
    }

    // Only process new widgets that haven't been processed yet
    const newWidgets = popoutWidgets.filter(
      (widget) => !processedWidgets.current.has(widget.id)
    );

    newWidgets.forEach((widget) => {
      const WidgetComponent =
        componentMap[widget.id as keyof typeof componentMap];

      if (!WidgetComponent) {
        console.error(`No component found for widget ID: ${widget.id}`);
        return;
      }

      openPopoutWindow({
        title: widget.name,

        onContainerReady: (container, window) => {
          const SearchComponent =
            componentMap[widget.component as keyof typeof componentMap]
              .SearchComponent || null;

          setWidgetPortals((prev) => [
            ...prev,
            [
              <PortalProvider container={window.document.body} key={widget.id}>
                <DensityProvider>
                <WidgetContainer
                  widget={widget}
                  widgetCompositeId={widget.widgetCompositeId || widget.id}
                  onClose={() => removePopoutWidget(widget.id)}
                  searchComponent={SearchComponent}
                >
                  {(headerOptions) => (
                    <WidgetComponent
                      headerOptions={headerOptions}
                      widgetCompositeId={widget.widgetCompositeId || widget.id}
                    />
                  )}
                </WidgetContainer>
                </DensityProvider>
              </PortalProvider>,
              container,
            ],
          ]);

          // Mark this widget as processed
          processedWidgets.current.add(widget.id);

          window.addEventListener("beforeunload", () => {
            removePopoutWidget(widget.id);
            processedWidgets.current.delete(widget.id);
          });
        },
      });
    });

    const processed = processedWidgets.current;

    // Cleanup function
    return () => {
      // Remove widgets that are no longer in the store
      const currentWidgetIds = new Set(popoutWidgets.map((w) => w.id));

      processed.forEach((widgetId) => {
        if (!currentWidgetIds.has(widgetId)) {
          processed.delete(widgetId);
        }
      });
    };
  }, [popoutWidgets, removePopoutWidget]);

  return (
    <>
      {widgetPortals.map((widgetPortal, index) =>
        ReactDOM.createPortal(
          widgetPortal[0],
          widgetPortal[1],
          `portal-${index}`
        )
      )}
    </>
  );
}

export default PopoutWidgets;
